export type MetricOrientation = '>' | '<' | '><' | '<>';
export type Cadence = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type MetricOrigin = 'input' | 'formula' | 'aggregate';
export type MetricIndicator = 'leading' | 'lagging';
export type AggregateFunction = 'sum' | 'mean' | 'median' | 'minimum' | 'maximum' | 'last';
export type IncompleteDataPolicy = 'require_complete' | 'ignore_missing';

export type FormulaExpression =
  | { type: 'constant'; value: number }
  | { type: 'metric'; metricId: string }
  | { type: 'previous'; metricId: string }
  | { type: 'operation'; operator: '+' | '-' | '*' | '/'; left: FormulaExpression; right: FormulaExpression }
  | {
      type: 'aggregate';
      function: AggregateFunction;
      metricId: string;
      trailingPeriods?: number;
    };

export interface AggregateDefinition {
  sourceMetricId: string;
  function: AggregateFunction;
  trailingPeriods?: number;
  incompletePolicy: IncompleteDataPolicy;
}

export const CADENCES: readonly Cadence[] = ['weekly', 'monthly', 'quarterly', 'yearly'];

export const CADENCE_LABEL: Record<Cadence, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Annual',
};

export const CADENCE_RANK: Record<Cadence, number> = { weekly: 0, monthly: 1, quarterly: 2, yearly: 3 };
export const CADENCE_NOUN: Record<Cadence, string> = { weekly: 'week', monthly: 'month', quarterly: 'quarter', yearly: 'year' };

export interface TargetEntry {
  target: number;
  targetHigh?: number;
  orientation: MetricOrientation;
  startPeriod: string | null;
  endPeriod: string | null;
}

export interface Scorecard {
  id: string;
  workspaceId: string;
  /** The team this board belongs to (TEAMS.md § 4.3.1). Absent means the root. */
  teamId?: string;
  name: string;
  createdAt: string;
  /** Archived boards are read-only and hidden from the picker by default (#263). */
  status: 'active' | 'archived';
  archivedAt?: string | null;
  archivedBy?: string | null;
}

export interface MetricEntry {
  period: string;
  value: number | null;
  /** Null where the value predates `metric_entries`; read as "not recorded". */
  enteredBy?: string | null;
  enteredAt?: string | null;
}

export interface Metric {
  id: string;
  /**
   * The team this entity lives in — its one home (TEAMS.md § 4.1).
   *
   * Optional only for the moment: the column is NOT NULL in the database (C2),
   * but rows created client-side before the store started sending it would have
   * no value here. Treat absent as "the root".
   */
  teamId?: string;
  title: string;
  unit?: string;
  decimals?: number;
  ownerId?: string;
  goalIds: string[];
  entries: MetricEntry[];
  origin: MetricOrigin;
  formulaExpression?: FormulaExpression;
  aggregate?: AggregateDefinition;
  startPeriod?: string;
  openingValue?: number;
  catalogKey?: string;
  cadence: Cadence;
  /** Null until someone classifies the metric. */
  indicator: MetricIndicator | null;
  rockIds: string[];
  targets: TargetEntry[];
}

export interface ScorecardMetric {
  id: string;
  scorecardId: string;
  metricId: string;
}
